import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { setCurrentTaxYear } from '../services/TaxYearHeader';
import { SESSION_CONSTANT } from '../../shared/utils/SessionConstants';
import { CORE_CONSTANT } from '../../shared/utils/CommonConstants';
import { offSessionReset, onSessionReset } from '../../shared/utils/SessionReset';

interface SelectedClient {
  id: string;
  name: string;
}

interface SelectedPartnership {
  id: string;
  name: string;
  entityType: string;
}

interface PartnershipContextValue {
  taxYear: string;
  setTaxYear: (year: string) => void;
  selectedClient: SelectedClient | null;
  setSelectedClient: (client: SelectedClient | null) => void;
  selectedPartnership: SelectedPartnership | null;
  setSelectedPartnership: (partnership: SelectedPartnership | null) => void;
  contextInitError: boolean;
  isContextReady: boolean;
}

const PartnershipContext = createContext<PartnershipContextValue | null>(null);

const STORAGE_KEY = SESSION_CONSTANT.PARTNERSHIP_CONTEXT_KEY;

export function PartnershipProvider({ children }: { children: React.ReactNode }) {
  const [taxYear, setTaxYearState] = useState<string>(() => {
    let initial: string = CORE_CONSTANT.PARTNERSHIP_CONTEXT.DEFAULT_TAX_YEAR;
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : null;
      if (parsed?.taxYear) initial = parsed.taxYear;
    } catch (err) {
      console.error('[PartnershipContext] Failed to read stored tax year:', err);
    }
    setCurrentTaxYear(initial);
    return initial;
  });
  const setTaxYear = useCallback((year: string) => {
    setCurrentTaxYear(year);
    setTaxYearState(year);
  }, []);
  const [selectedClient, setSelectedClient] = useState<SelectedClient | null>(null);
  const [selectedPartnership, setSelectedPartnership] = useState<SelectedPartnership | null>(null);
  const [contextInitError, setContextInitError] = useState(false);
  const [isContextReady, setIsContextReady] = useState(false);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.taxYear) setTaxYear(parsed.taxYear);
        if (parsed.selectedClient) setSelectedClient(parsed.selectedClient);
        if (parsed.selectedPartnership) setSelectedPartnership(parsed.selectedPartnership);
      }
    } catch (err) {
      console.error('[PartnershipContext] Failed to initialize:', err);
      setContextInitError(true);
    }
    setIsContextReady(true);
  }, [setTaxYear]);

  // A logout clears the selection held in memory as well as the stored one.
  useEffect(() => {
    const handleReset = () => {
      setSelectedClient(null);
      setSelectedPartnership(null);
      setTaxYear(CORE_CONSTANT.PARTNERSHIP_CONTEXT.DEFAULT_TAX_YEAR);
    };
    onSessionReset(handleReset);
    return () => offSessionReset(handleReset);
  }, [setTaxYear]);

  useEffect(() => {
    try {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ taxYear, selectedClient, selectedPartnership })
      );
    } catch (err) {
      console.error('[PartnershipContext] Failed to persist selection:', err);
    }
  }, [taxYear, selectedClient, selectedPartnership]);

  return (
    <PartnershipContext.Provider
      value={{
        taxYear, setTaxYear,
        selectedClient, setSelectedClient,
        selectedPartnership, setSelectedPartnership,
        contextInitError,
        isContextReady,
      }}
    >
      {children}
    </PartnershipContext.Provider>
  );
}

export function usePartnershipContext(): PartnershipContextValue {
  const ctx = useContext(PartnershipContext);
  if (!ctx) throw new Error('usePartnershipContext must be used within PartnershipProvider');
  return ctx;
}
